import classNames from 'classnames';
import { useCallback } from 'react';
import { Slider as RASlider, SliderThumb, SliderTrack, type SliderTrackRenderProps } from 'react-aria-components';
import { getValueIndex, SharedSliderProps, thumbSize } from './sharedSliderHelpers';
import SliderHeader from './SliderHeader';
import SliderPoints from './SliderPoints';
import styles from './styles/styles.module.scss';

type SliderProps = SharedSliderProps<number> & { isReversed?: boolean };

const Slider = ({
  className,
  disabled,
  formatHeaderValue,
  headerValueTextVariant,
  isReversed,
  label,
  labelTextVariant,
  marks,
  onChange,
  onChangeEnd,
  subHeader,
  subHeaderTextVariant,
  testId,
  value
}: SliderProps) => {
  const sliderTrack = useCallback(
    (props: SliderTrackRenderProps) => {
      const { state } = props;
      const lowerPercent = isReversed ? state.getThumbPercent(0) : 0;
      const upperPercent = isReversed ? 1 : state.getThumbPercent(0);
      const widthPercent = (upperPercent - lowerPercent) * 100;
      const width = `calc(${widthPercent}% + ${thumbSize / 2}px)`; // See comment in the scss for explanation of the calc call

      const valueIndex = state.getThumbValue(0);

      return (
        <>
          <div className={styles.track} />
          <div className={classNames(styles.trackFill, { [styles.reversed]: isReversed })} style={{ width }} />
          <SliderPoints getIsFilled={index => (isReversed ? index >= valueIndex : index <= valueIndex)} marks={marks} />
          {/* The library applies the SliderThumb transform via style not className, so we have to do the same to override it without !important */}
          <SliderThumb className={styles.thumb} index={0} style={{ transform: 'translateX(-50%)' }} />
        </>
      );
    },
    [isReversed, marks]
  );

  return (
    <RASlider
      className={classNames(styles.container, className)}
      isDisabled={disabled}
      maxValue={marks.length - 1}
      minValue={0}
      onChange={index => {
        onChange(marks[index].value);
      }}
      onChangeEnd={index => {
        onChangeEnd?.(marks[index].value);
      }}
      value={getValueIndex(marks, value)}
      data-testid={testId}
    >
      <SliderHeader
        headerValueTextVariant={headerValueTextVariant}
        label={label}
        labelTextVariant={labelTextVariant}
        sliderOutputChildren={
          formatHeaderValue
            ? ({ state }) => {
                const index = state.getThumbValue(0);
                return formatHeaderValue(marks[index].value);
              }
            : undefined
        }
        subHeader={subHeader}
        subHeaderTextVariant={subHeaderTextVariant}
      />
      <SliderTrack className={styles.trackContainer}>{sliderTrack}</SliderTrack>
    </RASlider>
  );
};

export default Slider;
